/**
 * defer_dispose.ts — the SpaceModule GPU-teardown LAW, shared.
 *
 * Never dispose() a GPU resource the same frame it may still ride an
 * in-flight submit: on real GPUs that is the "Buffer used in submit while
 * destroyed" device error and a black screen (field-hit leaving battles
 * 2026-08-12, and again on the berth cone→GLB upgrade 2026-08-20). Remove
 * the object from the scene NOW (deferDisposeTree does this itself); free
 * its resources ~350 ms later, past any queued command buffer.
 *
 * markShared() stamps a cached master's resources so clones can be torn
 * down freely — disposing a clone's geometry would otherwise destroy the
 * buffers every other instance (and the master itself) still uses.
 */
import * as THREE from 'three/webgpu';
import { afterSubmitWindow } from './gpu_dispose.js';

/** Stamp every geometry/material/texture under a cached master as SHARED. */
export function markShared(root: THREE.Object3D): void {
  root.traverse((o) => {
    const m = o as THREE.Mesh;
    const g = m.geometry as THREE.BufferGeometry | undefined;
    if (g) g.userData.shared = true;
    const mm = m.material as THREE.Material | THREE.Material[] | undefined;
    for (const mat of Array.isArray(mm) ? mm : mm ? [mm] : []) {
      mat.userData.shared = true;
      const sm = mat as THREE.MeshStandardMaterial;
      for (const tex of [sm.map, sm.normalMap, sm.emissiveMap, sm.roughnessMap, sm.aoMap]) {
        if (tex) tex.userData.shared = true;
      }
    }
  });
}

/**
 * Detach the tree from the scene NOW and free its OWNED resources after the
 * submit window. The detach lives HERE, not in the callers: CivicDressing's
 * undress / banner / bunting / treaty-stone paths (and a few vehicle, ship
 * and droid teardowns) called this without removing the group, so the meshes
 * stayed in the scene and were drawn every frame after their buffers were
 * freed — the field "[DrawGuard] skipped a draw whose GPU buffers are gone"
 * flood of 2026-09-02 (notice-board posts and bunting flags). A tree whose
 * geometry is being freed can never be drawn again, so detaching is always
 * right and idempotent for callers that already removed it.
 */
export function deferDisposeTree(root: THREE.Object3D, delayMs = 350): void {
  root.removeFromParent();
  const batch: Array<{ dispose(): void }> = [];
  const seenG = new Set<THREE.BufferGeometry>();
  const seenM = new Set<THREE.Material>();
  root.traverse((o) => {
    const m = o as THREE.Mesh;
    // A THREE.Sprite's geometry is three's ONE process-wide quad (2026-09-25): freeing it kills every sprite in
    // the app (the 2026-09-12 sector-root incident, seen again in the racing plates). Its material still goes.
    const g = (o as THREE.Sprite).isSprite ? undefined : (m.geometry as THREE.BufferGeometry | undefined);
    if (g && !g.userData.shared && !seenG.has(g)) { seenG.add(g); batch.push(g); }
    const mm = m.material as THREE.Material | THREE.Material[] | undefined;
    for (const mat of Array.isArray(mm) ? mm : mm ? [mm] : []) {
      if (mat.userData.shared || seenM.has(mat)) continue;
      seenM.add(mat);
      const sm = mat as THREE.MeshStandardMaterial;
      for (const tex of [sm.map, sm.normalMap, sm.emissiveMap, sm.roughnessMap, sm.aoMap]) {
        if (tex && !tex.userData.shared) batch.push(tex);
      }
      batch.push(mat);
    }
  });
  if (batch.length === 0) return;
  if ((globalThis as { __noFree?: boolean }).__noFree) return; // DIAG (see gpu_dispose)
  // frames+time gate (not wall-clock alone): see afterSubmitWindow — a swap
  // frame can run multi-second, outliving any fixed timer.
  afterSubmitWindow(() => {
    for (const d of batch) { try { d.dispose(); } catch { /* already freed */ } }
  }, 8, Math.max(delayMs, 1200));
}
